import { useState } from 'react';
import { Check, EyeOff, MessageSquarePlus } from 'lucide-react';
import { Button, EmptyState, ErrorState, Skeleton, StatusBadge, SubBar } from '@/design/components';
import { formatDate } from '@/lib/dates';
import { SurveyQuestions } from './SurveyQuestions';
import { errorMessage } from '@/lib/errors';
import type { Json } from '@/lib/database.types';
import {
  isComplete,
  useFeedbackCategories,
  useMySurveys,
  useSubmitFeedback,
  useSubmitSurvey,
  type Survey,
  type SurveyAnswers,
} from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/** סקרים ותיבת משוב (PRD 5.10.5). */
export function SurveysPage() {
  const surveys = useMySurveys();

  if (surveys.isPending) return <Skeleton className="m-4 h-40" />;
  if (surveys.isError) return <ErrorState onRetry={() => void surveys.refetch()} />;

  const open = (surveys.data ?? []).filter((survey) => !survey.answered);
  const answered = (surveys.data ?? []).filter((survey) => survey.answered);

  return (
    <div className="grid content-start gap-4">
      <SubBar title="סקרים ומשוב" subtitle="מה שלא נאמר במשמרת" />

      <div className="grid gap-6 px-4 pb-8">
        {open.length === 0 && answered.length === 0 && (
          <EmptyState title="אין סקרים פתוחים" description="כשיהיה סקר הוא יופיע כאן." />
        )}

        {open.map((survey) => (
          <SurveyCard key={survey.survey_id} survey={survey} />
        ))}

        {answered.length > 0 && (
          <section className="grid gap-2">
            <h2 className="m-0 font-display text-display-m font-semibold">ענית</h2>
            {answered.map((survey) => (
              <div
                key={survey.survey_id}
                className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-raised p-3"
              >
                <span className="grow text-body text-ink">{survey.title}</span>
                <StatusBadge tone="good">נשלח</StatusBadge>
              </div>
            ))}
          </section>
        )}

        <FeedbackBox />
      </div>
    </div>
  );
}

function SurveyCard({ survey }: { survey: Survey }) {
  const submit = useSubmitSurvey();
  const [answers, setAnswers] = useState<SurveyAnswers>({});
  const [error, setError] = useState<string | null>(null);

  // עדכון פונקציונלי ולא פרישה של answers: שתי תשובות שנרשמות
  // באותו tick נבנות שתיהן מאותו מצב ישן, והשנייה דורסת את
  // הראשונה בשקט
  const set = (questionId: string, value: Json) =>
    setAnswers((prev) => ({ ...prev, [questionId]: value }));

  const ready = isComplete(survey.questions, answers);

  return (
    <section className="grid gap-3 rounded-md border border-line bg-raised p-3">
      <div className="grid gap-1">
        <h2 className="m-0 font-display text-display-m font-semibold">{survey.title}</h2>
        {survey.description && <p className="m-0 text-body-sm text-muted">{survey.description}</p>}
        {survey.ends_at && (
          <p className="m-0 text-label text-muted">פתוח עד {formatDate(survey.ends_at)}</p>
        )}
      </div>

      {survey.is_anonymous && (
        <p className="m-0 flex items-start gap-2 rounded-sm border border-line p-2 text-body-sm">
          <EyeOff size={18} strokeWidth={1.75} aria-hidden="true" className="mt-0.5 shrink-0" />
          <span>
            הסקר אנונימי. התשובה נשמרת בלי השם שלך, ונשמר רק הסניף. ההשתתפות נרשמת בנפרד, כדי לא
            לשלוח לך תזכורת.
          </span>
        </p>
      )}

      <SurveyQuestions questions={survey.questions} answers={answers} onChange={set} />

      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      <Button
        variant="primary"
        disabled={!ready || submit.isPending}
        onClick={() => {
          setError(null);
          submit
            .mutateAsync({ surveyId: survey.survey_id, answers })
            .catch((caught) => setError(errorMessage(caught)));
        }}
      >
        <Check size={20} strokeWidth={1.75} aria-hidden="true" />
        שליחת התשובות
      </Button>
    </section>
  );
}

/** תיבת המשוב: אפשר לשלוח בשם או בלי. */
function FeedbackBox() {
  const categories = useFeedbackCategories();
  const submit = useSubmitFeedback();
  const [body, setBody] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <section className="grid gap-3 rounded-md border border-line bg-raised p-3">
      <h2 className="m-0 flex items-center gap-2 font-display text-display-m font-semibold">
        <MessageSquarePlus size={20} strokeWidth={1.75} aria-hidden="true" />
        תיבת משוב
      </h2>

      {done ? (
        <div className="grid gap-2">
          <StatusBadge tone="good" className="w-fit">
            המשוב נשלח
          </StatusBadge>
          <Button
            onClick={() => {
              setDone(false);
              setBody('');
              setAnonymous(false);
            }}
          >
            לשלוח עוד משוב
          </Button>
        </div>
      ) : (
        <>
          <div className="grid gap-1">
            <label htmlFor="feedback-category" className="text-body-sm text-muted">
              נושא
            </label>
            <select
              id="feedback-category"
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
              className={FIELD}
            >
              <option value="">בלי נושא</option>
              {categories.data?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-1">
            <label htmlFor="feedback-body" className="text-body-sm text-muted">
              מה רצית לומר
            </label>
            <textarea
              id="feedback-body"
              rows={4}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              className={`${FIELD} py-2`}
            />
          </div>

          <label className="flex items-center gap-2 text-body">
            <input
              type="checkbox"
              checked={anonymous}
              onChange={(event) => setAnonymous(event.target.checked)}
              className="size-5"
            />
            לשלוח בלי השם שלי
          </label>
          {anonymous && (
            <p className="m-0 text-label text-muted">
              לא יישמרו לא השם ולא הסניף. אי אפשר יהיה לחזור אליך עם תשובה.
            </p>
          )}

          {error && (
            <p role="alert" className="m-0 text-body-sm text-bad">
              {error}
            </p>
          )}

          <Button
            variant="primary"
            disabled={!body.trim() || submit.isPending}
            onClick={() => {
              setError(null);
              submit
                .mutateAsync({ body, categoryId: categoryId || null, anonymous })
                .then(() => setDone(true))
                .catch((caught) => setError(errorMessage(caught)));
            }}
          >
            שליחת המשוב
          </Button>
        </>
      )}
    </section>
  );
}
